import type { TerminalOutputChunk } from "@/lib/terminal";

/** 1コマンドの転写に保存する PTY 出力の上限。超過分は ANSI エスケープが途中で切れて描画が壊れる。 */
export const TERMINAL_COMMAND_TRANSCRIPT_MAX_BYTES = 512 * 1024;

const TRANSCRIPT_OVERFLOW_PLACEHOLDER =
	"出力が512KiBを超えたため、再読み込み後の履歴には表示されません。";

export interface TerminalTranscriptRecording {
	/** 復元用の色付きチャンク。上限超過時は muted プレースホルダ1行のみ。 */
	chunks: TerminalOutputChunk[];
	/** バイト出力を1回でも受け取ったか。 */
	sawOutput: boolean;
}

/**
 * PTY 出力バイト列を蓄積し、リロード後の履歴復元用テキストへ変換する。フレームを跨ぐマルチバイト文字を
 * 壊さないよう streaming TextDecoder で逐次デコードし、finish() で残りを flush する。
 * 512KiB を超えたら全出力を破棄してプレースホルダ1行へ置き換える (途中で切ると ANSI が壊れるため all-or-nothing)。
 */
export class TerminalTranscriptRecorder {
	private readonly decoder = new TextDecoder();
	private text = "";
	private byteLength = 0;
	private sawOutput = false;
	private overflowed = false;

	note(bytes: Uint8Array): void {
		if (bytes.byteLength === 0) return;
		this.sawOutput = true;
		if (this.overflowed) return;
		this.byteLength += bytes.byteLength;
		if (this.byteLength > TERMINAL_COMMAND_TRANSCRIPT_MAX_BYTES) {
			this.overflowed = true;
			this.text = "";
			return;
		}
		this.text += this.decoder.decode(bytes, { stream: true });
	}

	finish(): TerminalTranscriptRecording {
		const tail = this.decoder.decode();
		if (this.overflowed) {
			return {
				chunks: [{ text: `${TRANSCRIPT_OVERFLOW_PLACEHOLDER}\n`, color: "muted" }],
				sawOutput: this.sawOutput,
			};
		}
		const text = this.text + tail;
		return {
			chunks: text.length > 0 ? [{ text, color: "default" }] : [],
			sawOutput: this.sawOutput,
		};
	}
}
